import { useEffect, useState } from 'react'
import { API_URL } from '@/config/api'

// codex #8213 r2 (minor): NOT authFetch. On a 401 authFetch clears the tokens and redirects to /login WITHOUT the
// simulator return target, so an expired token or a refusal (HANDOFF_NO_USER, HANDOFF_USER_NOT_APPROVED) would lose
// both the destination and the reason. The hand-off request is plain fetch with the bearer header, and every refusal,
// 401 included, is shown here.
function handoffUrl(): string {
  return API_URL.endsWith('/api') ? `${API_URL}/auth/handoff` : `${API_URL}/api/auth/handoff`
}
async function requestHandoffCode(body: { target: string; stateHash: string }): Promise<Response> {
  const token = localStorage.getItem('jwt') || localStorage.getItem('auth_token')
  if (!token) throw new Error('you are not signed in on this browser (no session token, HANDOFF_BEARER_REQUIRED)')
  return fetch(handoffUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  })
}

// Jaume 2026-11-04 (card login-cross-domain-dead-file (a)), #8188: a signed-in user returning to the onboarding
// simulator gets a ONE-TIME CODE in the URL, never a token. The backend issues it (POST /api/auth/handoff, single use,
// 60 s, bound to this user and the target origin); the simulator's own server exchanges it for the token, which only
// ever travels in a response body. Before this, login sent the user back with nothing and the simulator looped (#8188);
// the unwired login-cross-domain.tsx this replaces put the auth_token itself in the URL.
//
// Security review of PR #8213: the simulator tab starts the round trip and puts the SHA-256 of a secret it keeps in
// ?hs= (lib/simulatorHandoff.ts); the code is bound to it, so a code alone cannot be exchanged (login CSRF, a leaked
// code). The code travels in the URL FRAGMENT (#handoff=), never sent to servers or in Referer.
// A failure is shown and logged, and the user is NOT sent on without a code: that would only restart the loop.
const STATE_HASH = /^[a-f0-9]{64}$/
export function ExternalHandoff({ href }: { href: string }) {
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const target = new URL(href)
    const stateHash = target.searchParams.get('hs')
    target.searchParams.delete('hs')
    ;(async () => {
      try {
        if (!stateHash || !STATE_HASH.test(stateHash)) {
          throw new Error('this sign-in was not started from the simulator page (no hand-off state, HANDOFF_STATE_MISSING)')
        }
        const response = await requestHandoffCode({ target: target.origin, stateHash })
        const body = await response.json().catch(() => ({}))
        if (!response.ok || typeof body.code !== 'string') {
          throw new Error(body.error ? `${body.error} (${body.code || response.status})` : `HTTP ${response.status}`)
        }
        if (cancelled) return
        target.hash = new URLSearchParams({ handoff: body.code }).toString()
        window.location.href = target.toString()
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e)
        console.error(`[Login] hand-off to ${target.origin} FAILED: ${message}`)
        if (!cancelled) setError(message)
      }
    })()
    return () => { cancelled = true }
  }, [href])

  if (error) {
    return (
      <div role="alert" className="max-w-md mx-auto mt-16 p-6 rounded border border-red-300 bg-red-50 text-red-800">
        <p className="font-semibold">You are signed in, but we could not hand your session to {new URL(href).origin}.</p>
        <p className="mt-2 text-sm">{error}</p>
        <a className="mt-4 inline-block underline" href="/dashboard">Go to the dashboard</a>
      </div>
    )
  }
  return <p className="text-center mt-16 text-gray-600">Returning you to {new URL(href).origin}…</p>
}
